import { type BlockConfig, iconField } from './_types';

/**
 * Payload `Block` config sidecar for the `b2bUseCases` block.
 *
 * Decomposed from `src/components/b2b/B2BUseCases.tsx`. Tabs-driven
 * use-case showcase with per-tab benefits + example callout.
 *
 * Slug + interfaceName naming reconciliation:
 *   File name:               `b2b-use-cases.tsx` / `b2b-use-cases.payload.ts`
 *   React component export:  `B2BUseCases`
 *   Payload `slug`:          `b2bUseCases`
 *   Payload `interfaceName`: `B2BUseCasesBlock`
 */

export const B2BUseCasesBlock: BlockConfig = {
  slug: 'b2bUseCases',
  interfaceName: 'B2BUseCasesBlock',
  labels: {
    singular: 'B2B Use Cases',
    plural: 'B2B Use Cases Blocks',
  },
  fields: [
    {
      name: 'eyebrow',
      type: 'text',
      label: 'Eyebrow',
      defaultValue: 'Use Cases',
    },
    {
      name: 'headline',
      type: 'textarea',
      required: true,
      label: 'Headline',
      admin: { rows: 2 },
    },
    {
      name: 'subheadline',
      type: 'textarea',
      label: 'Subheadline',
      admin: { rows: 2 },
    },
    {
      name: 'benefitsHeading',
      type: 'text',
      label: 'Benefits column heading',
      defaultValue: 'Key Benefits',
    },
    {
      name: 'exampleHeading',
      type: 'text',
      label: 'Example column heading',
      defaultValue: 'Example',
    },
    {
      name: 'useCases',
      type: 'array',
      required: true,
      minRows: 1,
      maxRows: 8,
      label: 'Use cases',
      labels: { singular: 'Use case', plural: 'Use cases' },
      fields: [
        {
          name: 'id',
          type: 'text',
          required: true,
          label: 'Tab id',
          admin: {
            description:
              'Unique identifier used as the Radix Tabs `value` (e.g. "ml-ai", "audience"). Must be unique within this block.',
          },
        },
        iconField({
          name: 'icon',
          label: 'Icon (lucide-react name)',
          required: true,
        }),
        { name: 'title', type: 'text', required: true, label: 'Title' },
        {
          name: 'description',
          type: 'text',
          required: true,
          label: 'Description',
        },
        {
          name: 'products',
          type: 'array',
          label: 'Products (badges)',
          maxRows: 6,
          fields: [
            { name: 'text', type: 'text', required: true, label: 'Product' },
          ],
        },
        {
          name: 'benefits',
          type: 'array',
          required: true,
          minRows: 1,
          maxRows: 12,
          label: 'Benefits',
          labels: { singular: 'Benefit', plural: 'Benefits' },
          fields: [
            { name: 'text', type: 'text', required: true, label: 'Benefit' },
          ],
        },
        {
          name: 'example',
          type: 'textarea',
          label: 'Example callout',
          admin: { rows: 3 },
        },
      ],
    },
  ],
};

export default B2BUseCasesBlock;
